<script setup lang="ts">
import { computed } from 'vue';
import type { TaskItem } from '@Types/dashboard.types';
import type { EsignCompletionTaskData } from '@Types/completionTaskData.types';
import TaskCompletionHero from '@Components/completion/TaskCompletionHero.vue';
import DownloadCard from '@Components/completion/DownloadCard.vue';
import IllustrationDocumentSignature
    from '@Components/priming/illustrations/IllustrationDocumentSignature.vue';
import { formatLongDate } from '@Src/shared/utils/formatDate';

const props = defineProps<{
    task: TaskItem;
}>();

const data = computed<EsignCompletionTaskData>(() =>
    (props.task.taskData ?? {}) as EsignCompletionTaskData,
);

const signedOn = computed(() =>
    formatLongDate(data.value.signed_at ?? props.task.completedDate),
);

const documentCount = computed(() => data.value.documents?.length ?? 0);
</script>

<template>
    <div class="escv">
        <TaskCompletionHero status-icon="success">
            <template #illustration>
                <IllustrationDocumentSignature />
            </template>
            Your documents are signed
        </TaskCompletionHero>

        <div class="escv__body">
            <p class="escv__message">
                Thanks for signing. Your executed documents and the certificate
                of completion are ready to download below.
            </p>

            <div class="escv__downloads">
                <DownloadCard
                    label="Signed document"
                    icon="cd-ic-document"
                    analytics-key="esign-signed-document"
                />
                <DownloadCard
                    label="Certificate of completion"
                    icon="cd-ic-check-circle"
                    analytics-key="esign-certificate-of-completion"
                />
            </div>

            <p v-if="signedOn || documentCount" class="escv__meta">
                <span v-if="documentCount">
                    {{ documentCount }} {{ documentCount === 1 ? 'document' : 'documents' }} signed
                </span>
                <span v-if="signedOn && documentCount" class="escv__meta-sep">·</span>
                <span v-if="signedOn">Signed {{ signedOn }}</span>
            </p>
        </div>
    </div>
</template>

<style lang="scss" scoped>
@use 'certifid-v2-client-hub-styles' as *;

.escv {
    display: flex;
    flex-direction: column;

    &__body {
        display: flex;
        flex-direction: column;
        gap: 20px;
        padding: 20px $portal-section-px 0;

        @media (min-width: 768px) {
            padding: 20px 32px 0;
        }
    }

    &__message {
        font-size: $text-portal-body-lg;
        color: $portal-text;
        line-height: 1.5;
        margin: 0;
    }

    &__downloads {
        display: flex;
        flex-direction: column;
        gap: 12px;
    }

    &__meta {
        font-size: $text-portal-small;
        color: $portal-text-mid;
        text-align: center;
        margin: 0;
        line-height: 1.5;
    }

    &__meta-sep {
        margin: 0 8px;
    }
}
</style>
